import React from "react";
import { useAuthenticated } from "@gettactic/hooks";
import { OrgLogo } from "@cmp-src/organizations/OrgLogo/OrgLogo";
import { getOrganizationUrl } from "@gettactic/helpers/src/url/getOrganizationUrl";
import { ScrollArea } from "@cmp-src/primitives/scroll-area";
import { cn } from "@gettactic/helpers";
import { CloseIcon } from "@cmp-src/icons/icons";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";
import { useGlobalEscape } from "@gettactic/helpers/src/hooks/useGlobalEscape";
import {
  isVercelDeployment,
  setVercelSlug
} from "@gettactic/helpers/src/vercel/vercelPreview";
import { Link } from "@cmp-src/primitives/Link";
import { getFocusableElements } from "@cmp-src/mobile/MobileDrawer/helpers";
import { createPortal } from "react-dom";
export interface OrganizationSwitcherProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const Classes = {
  Overlay: cn(
    "fixed inset-0 z-modal bg-black/50 backdrop-blur-sm ",
    "data-[state=open]:fade-in-0 data-[state=open]:animate-in",
    "data-[state=closed]:fade-out-0 data-[state=closed]:animate-out"
  ),
  Panel: cn(
    "fixed top-0 bottom-0 left-0 z-modal flex w-full md:max-w-[500px] flex-col bg-white shadow-xl",
    "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:fade-in-0 data-[state=open]:animate-in",
    "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out"
  ),
  Header: "flex items-center justify-between border-neutral-200 px-7 pt-6",
  CloseButton: "p-4",
  CloseIcon: "size-4",
  ScrollContent: "flex flex-col gap-6 p-6",
  OrgButton:
    "relative flex min-h-20 cursor-pointer items-center gap-4 rounded-xl border border-neutral-200 p-4 hover:bg-neutral-50",
  LogoImage: "h-auto w-10",
  LogoIcon: "size-10"
};

/**
 * A panel listing the signed-in user's organizations (not guest ones), each linking to its
 * dashboard; on Vercel previews it swaps the organization in place.
 */
export const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({
  isOpen,
  onOpenChange
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();

  useGlobalEscape(() => {
    onOpenChange(false);
  });

  const { isVisible } = useCloseAnimationTimeout(isOpen);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const titleId = React.useId();

  // Focus moves into the panel when it opens and back to where it was when it closes
  React.useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusTimer = requestAnimationFrame(() => panelRef.current?.focus());
    return () => {
      cancelAnimationFrame(focusTimer);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [isOpen]);

  // Keeps Tab inside the panel, as in any modal dialog
  const trapTabKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    const focusables = getFocusableElements(panelRef.current) as HTMLElement[];
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Vercel previews have no per-org subdomain to redirect to, so switching org
  // by navigating to `{slug}.domain` (the default below) would hit a dead host.
  // Instead swap the override slug saved in localStorage and hard-reload, so the
  // app re-reads it and rebuilds the API client for the selected organization.
  const isVercel = isVercelDeployment();

  if (!isVisible) return null;

  return createPortal(
    <div>
      <div
        className={Classes.Overlay}
        data-state={isOpen ? "open" : "closed"}
        onClick={() => onOpenChange(false)}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={trapTabKey}
        data-state={isOpen ? "open" : "closed"}
        className={cn(Classes.Panel, "focus:outline-none")}
      >
        <div className={Classes.Header}>
          <h2 id={titleId} className="font-semibold text-lg">
            Switch Organization
          </h2>
          <button
            type="button"
            aria-label="Close"
            className={Classes.CloseButton}
            onClick={() => onOpenChange(false)}
          >
            <CloseIcon aria-hidden className={Classes.CloseIcon} />
          </button>
        </div>
        <ScrollArea className="w-full flex-1">
          <div className={Classes.ScrollContent}>
            {(authenticatedUser.organizations || [])
              .filter((o) => o.role !== "guest")
              .map((organization) => (
                <Link
                  key={organization.id}
                  className={Classes.OrgButton}
                  href={
                    isVercel
                      ? "/"
                      : getOrganizationUrl(organization, "/dashboard")
                  }
                  onClick={
                    isVercel
                      ? (event) => {
                          event.preventDefault();
                          setVercelSlug(organization.slug || "");
                          window.location.href = "/";
                        }
                      : undefined
                  }
                >
                  <p className="text-neutral-500 text-sm">
                    <OrgLogo
                      logoUrl={organization.logo_url}
                      alt={organization.name ?? "Organization logo"}
                      className={Classes.LogoImage}
                      fallbackClassName={Classes.LogoIcon}
                    />
                  </p>
                  <h3 className="font-semibold text-lg">{organization.name}</h3>
                </Link>
              ))}
          </div>
        </ScrollArea>
      </div>
    </div>,
    document.body
  );
};
